'use client'

import { WidgetType } from '@lastbrand/storefront-proto-registry/api/v1/enums'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNextButton,
  CarouselPreviousButton,
} from '@lastbrand/web-ui/carousel'
import classnames from 'classnames'

import { MobileScrollCarousel } from '@src/components/blocks/product-list-section-two-ds/mobile-scroll-carousel'
import { PLBaseErrorBoundary } from '@src/components/pl-base-error-boundary'
import { PLTypedWidgetInterface, PLTypedWidgetProps } from '@src/components/pl-base-widget'
import { useWidget } from '@src/components/pl-base-widget/contexts/page-widget-store'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { useFreeDragCarousel } from '@src/ds/blocks/carousel-plugins'
import {
  matchesPlWidgetId,
  useStatsigPlWidgetId,
} from '@src/experiments/statsig-integration/use-statsig-pl-widget-ids-config.hook'
import LoaderWrapper from '@src/quince-components/src/atoms/loaderWrapper/LoaderWrapper'
import Swiper from '@src/quince-components/src/atoms/swiper'
import { DOTS_POSITION } from '@src/quince-components/src/atoms/swiper/Swiper'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import makeChunks from '@src/quince-site-common/src/utils/makeChunks'
import { SIMILAR_PRODUCTS_SECTION_NAME_FOR_REF } from '@src/quince-site-pdp/src/organisms/productPageSections/similarProducts/constant'
import similarStyles from '@src/quince-site-pdp/src/organisms/productPageSections/similarProducts/similarProducts.module.scss'
import { shouldUseDesignSystem } from '@src/utils/should-use-design-system'

import { CardDisplayOptionsProvider } from '../single-product-card/card-display-options-context'
import { CardTrackingProvider } from '../single-product-card/card-tracking-context'
import { CAROUSEL_ARROW_DIRECTION, useRecoBandTelemetry } from '../use-reco-band-telemetry'

import pgcStyles from './product-grid-carousel-pdp.module.scss'
import {
  carouselWrapperClass,
  headerRowClass,
  headingClass,
  mobileDotsRowCssRaw,
  mobileGridClass,
  navGroupClass,
  sectionClass,
} from './product-grid-carousel-pdp.styles'

import type { ProductGridCarouselWidgetData } from '@lastbrand/storefront-proto-registry/api/v1/widget'

const DESKTOP_SLIDES_TO_SCROLL = 4
// Cards per mobile slide (a 2×2 grid). Shared by the page chunking AND the absolute per-card
// testid numbering below — a single source so the emitted `<band>-products-NN` numbers can't
// diverge from the layout if the page size changes (mirrors ProductListSectionTwoDS's
// MOBILE_CAROUSEL_PAGE_SIZE).
const MOBILE_CAROUSEL_PAGE_SIZE = 4

/**
 * PDP product-grid-carousel container (v2) — QSAL shop_the_look / complete_the_look.
 *
 * DS path  — Figma "More to Explore" band (nodes 6236:51261 desktop / 6236:47019 mobile): an uppercase
 *             grosa eyebrow title + desktop nav arrows, a 1504-capped 4-up row on desktop, and a 2×2
 *             paged grid (4 cards/slide) with CarouselIndicator dots on mobile.
 * Non-DS path — ProductListTwo (existing non-PL layout, handles 2×2 mobile grid + arrows).
 *              Reads product data from props.widgets (nested SINGLE_PRODUCT_CARD widgets).
 */
function ProductGridCarouselPdp(props: PLTypedWidgetProps<ProductGridCarouselWidgetData>) {
  const data = props.data.productGridCarouselData
  const displayLimit = data?.displayLimit
  const isDS = shouldUseDesignSystem()
  const deviceInfo = useDeviceInfo()
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP
  // Swatch/color-change refetch in flight → overlay a spinner on the card area (SWR keeps this widget
  // mounted with `isRefreshing`), matching the non-PL similar-products loader. Key by `widget.id` (the
  // store's UUID key), not `widgetId` (API-request key) — see lazy-widget-fetcher.ts:141.
  const isRefreshing = useWidget(props.id)?.isRefreshing ?? false
  const loaderMinHeight = isDesktop ? '31rem' : '13.5rem'

  // Shop-Similar scroll target: expose the anchor only on the band whose DB id matches the `mte`
  // ("More to Explore") key in the `pl_widget_ids` Statsig config — reco bands share this widget type,
  // so target by id, not type. STRFRNT-15606.
  // "More to Explore" is a different widget/id per breakpoint — mobile renders it as this
  // PRODUCT_GRID_CAROUSEL, desktop as HORIZONTAL_PRODUCT_LIST — so read the DEVICE-SPECIFIC id from the
  // `pl_widget_ids` config: `mteMweb` on mobile, `mteDweb` on desktop. The Shop-Similar anchor lands on
  // whichever widget matches its device's id. STRFRNT-15606.
  // eslint-disable-next-line i18next/no-literal-string -- config keys, not user-facing copy
  const moreToExploreWidgetId = useStatsigPlWidgetId(isDesktop ? 'mteDweb' : 'mteMweb')
  const isMoreToExploreBand = matchesPlWidgetId(moreToExploreWidgetId, props.id, props.widgetId)

  // Star-rating visibility mirrors non-PL, which differs by band: "Shop the Look" (pairWithProducts)
  // hides ratings; "More to Explore" / "Quince Shoppers Also Love" show them. Swatches (`hideOptions`)
  // are hidden on every reco carousel. Hide ratings only on the Shop-the-Look band, identified via the
  // device-specific `stlDweb`/`stlMweb` pl_widget_ids keys — WEB-specific on purpose: the bare `stl`
  // key carries the APP layout's widget id (the app has its own id space), same reason `mte` (app)
  // coexists with `mteDweb`/`mteMweb` (web). Every other reco band shows ratings. STRFRNT-15585.
  // eslint-disable-next-line i18next/no-literal-string -- config keys, not user-facing copy
  const shopTheLookWidgetId = useStatsigPlWidgetId(isDesktop ? 'stlDweb' : 'stlMweb')
  const isShopTheLookBand = matchesPlWidgetId(shopTheLookWidgetId, props.id, props.widgetId)

  // "Quince Shoppers Also Love" / FBT band → non-PL `frequently_purchased_together` IS_VISIBLE. Same
  // BE-driven id resolution as stl/mte (device-specific `fbtDweb`/`fbtMweb` keys); inert until the
  // `pl_widget_ids` Statsig config carries the fbt id(s).
  // eslint-disable-next-line i18next/no-literal-string -- config keys, not user-facing copy
  const frequentlyBoughtTogetherWidgetId = useStatsigPlWidgetId(isDesktop ? 'fbtDweb' : 'fbtMweb')
  const isFrequentlyPurchasedTogetherBand = matchesPlWidgetId(
    frequentlyBoughtTogetherWidgetId,
    props.id,
    props.widgetId,
  )

  const allCards = props.renderedChildren ?? []
  const cards = displayLimit ? allCards.slice(0, displayLimit) : allCards

  // Reco telemetry (IA-only), shared with HORIZONTAL_PRODUCT_LIST so both PDP reco containers stay in
  // lockstep: `ref` fires the per-band IS_VISIBLE (mapped bands only) + `recommendationView` on
  // scroll-into-view; `onCarouselArrowClick` fires CAROUSEL_LEFT/RIGHT_CLICK on arrow navigation.
  const {
    ref: recommendationViewRef,
    onCarouselArrowClick,
    cardProductViewTracker,
    cardClickTrackConfig,
  } = useRecoBandTelemetry({
    isShopTheLookBand,
    isMoreToExploreBand,
    isFrequentlyPurchasedTogetherBand,
    bandTitle: data?.title,
    widgetId: props.widgetId,
    productCount: cards.length,
  })

  // Desktop-only: the DS mobile branch renders `MobileScrollCarousel`, which takes no Embla opts.
  const { opts: carouselOpts, plugins: carouselPlugins } = useFreeDragCarousel({
    slidesToScroll: DESKTOP_SLIDES_TO_SCROLL,
    hasOverflow: cards.length > DESKTOP_SLIDES_TO_SCROLL,
  })

  // ── Non-DS path: renderedChildren + SimilarProducts CSS ─────────────────────
  // Widget-of-widget: renderedChildren = pre-rendered SingleProductCardWidget nodes.
  // Desktop: variableWidth=false + numberOfVisibleSlides=4 → SwiperJS divides
  //   container into 4 equal slots; cards fill their slot via width:100%.
  // Mobile: cards chunked 4-per-slide into a 2×2 grid using repeat(2,1fr) —
  //   card fills its 1fr cell naturally, no explicit width class needed on the card.
  if (!isDS) {
    if (cards.length === 0) return null

    const mobileChunks = makeChunks([...cards], 4)

    return (
      <CardTrackingProvider productViewTracker={cardProductViewTracker} productClickTrackConfig={cardClickTrackConfig}>
        <div
          ref={recommendationViewRef}
          // Only the "More to Explore" band (id === Statsig `mte`) carries the Shop-Similar scroll anchor.
          data-section-name={isMoreToExploreBand ? SIMILAR_PRODUCTS_SECTION_NAME_FOR_REF : undefined}
          className={classnames(
            similarStyles.similarProducts,
            similarStyles.similarProducts__newPdpV2,
            pgcStyles.section,
          )}
        >
          {data?.title ? (
            <h2
              className={classnames(
                similarStyles.similarProducts__title,
                similarStyles.similarProducts__title__newPdpV2,
              )}
            >
              {data.title}
            </h2>
          ) : null}
          <LoaderWrapper showLoader={isRefreshing} minHeight={loaderMinHeight}>
            <Swiper
              // Stable key: keying by cards.length tore the whole Swiper down (destroy + re-init on
              // the main thread, mid-interaction) every time a dedupe pass trimmed the card set.
              key={props.id}
              className={classnames(similarStyles.productListSection, similarStyles.productListSection__newPdpDesignV2)}
              variableWidth={!isDesktop}
              // Desktop: freeMode OFF so the last group snaps flush and the final card isn't cropped at
              // the carousel end (matches non-PL arrow carousels). Mobile keeps free-swipe. STRFRNT-15716.
              freeMode={!isDesktop}
              showNavigator={isDesktop}
              numberOfVisibleSlides={isDesktop ? 4 : 1}
              showDots={!isDesktop}
              dotsPosition={DOTS_POSITION.BOTTOM}
              prevIconClass={similarStyles.prevIcon}
              nextIconClass={similarStyles.nextIcon}
              arrowOnClick={({ direction }) => {
                onCarouselArrowClick(direction)
              }}
              id={`pdp-pgc-${props.widgetId}`}
            >
              {isDesktop
                ? cards.map((child, index) => (
                    <div key={`${child.id || child.type}-${index}`} className={pgcStyles.desktopCard}>
                      {/* Reco carousel: hide swatches + ratings (non-PL similarProducts parity). */}
                      <CardDisplayOptionsProvider
                        hideOptions
                        hideReviews={isShopTheLookBand}
                        useRecommendationImageAspect={false}
                      >
                        {child.node}
                      </CardDisplayOptionsProvider>
                    </div>
                  ))
                : mobileChunks.map((chunk, chunkIndex) => (
                    <div key={chunkIndex} className={pgcStyles.mobileChunk}>
                      {chunk.map((child, index) => (
                        <div key={`${child.id || child.type}-${index}`}>
                          <CardDisplayOptionsProvider
                            hideOptions
                            hideReviews={isShopTheLookBand}
                            useRecommendationImageAspect={false}
                          >
                            {child.node}
                          </CardDisplayOptionsProvider>
                        </div>
                      ))}
                    </div>
                  ))}
            </Swiper>
          </LoaderWrapper>
        </div>
      </CardTrackingProvider>
    )
  }

  // ── DS path: DS Carousel ─────────────────────────────────────────────────
  if (cards.length === 0) return null

  const title = data?.title
  // Show arrows when there are more cards than fit in one view.
  // Use > 1 so arrows appear for any carousel with 2+ cards to scroll.
  const hasMore = cards.length > 1

  // Mobile pages 4 cards at a time as a 2×2 grid per slide (Figma node 6236:47019); desktop is one card
  // per slide (4-up).
  const mobilePages = !isDesktop ? makeChunks([...cards], MOBILE_CAROUSEL_PAGE_SIZE) : []

  const heading = title ? <div className={headingClass}>{title}</div> : <div />

  // QA locator root, keyed off the Statsig band id (never the CMS title). Unmapped bands share the
  // generic RECOMMENDATIONS id — disambiguate via DOM order. The root doubles as the numbered
  // per-card SECTION_PRODUCT prefix (e.g. 'more-to-explore-products-01'), with mobile pages
  // numbering by ABSOLUTE card order so desktop and mobile agree.
  const sectionTestId = isShopTheLookBand
    ? TEST_LOCATORS.PDP_SECTIONS.SHOP_THE_LOOK
    : isMoreToExploreBand
      ? TEST_LOCATORS.PDP_SECTIONS.MORE_TO_EXPLORE
      : isFrequentlyPurchasedTogetherBand
        ? TEST_LOCATORS.PDP_SECTIONS.FREQUENTLY_PURCHASED_TOGETHER
        : TEST_LOCATORS.PDP_SECTIONS.RECOMMENDATIONS

  return (
    // Shop-Similar scroll anchor only on the "More to Explore" band (id === Statsig `mte`). STRFRNT-15606.
    <CardTrackingProvider productViewTracker={cardProductViewTracker} productClickTrackConfig={cardClickTrackConfig}>
      <section
        ref={recommendationViewRef}
        className={sectionClass}
        data-section-name={isMoreToExploreBand ? SIMILAR_PRODUCTS_SECTION_NAME_FOR_REF : undefined}
        data-testid={sectionTestId}
      >
        {isDesktop ? (
          <Carousel opts={carouselOpts} plugins={carouselPlugins}>
            <div className={carouselWrapperClass}>
              <div className={headerRowClass}>
                {heading}
                {hasMore ? (
                  <div className={navGroupClass}>
                    <CarouselPreviousButton
                      onClick={() => {
                        onCarouselArrowClick(CAROUSEL_ARROW_DIRECTION.PREV)
                      }}
                    />
                    <CarouselNextButton
                      onClick={() => {
                        onCarouselArrowClick(CAROUSEL_ARROW_DIRECTION.NEXT)
                      }}
                    />
                  </div>
                ) : null}
              </div>
              <LoaderWrapper showLoader={isRefreshing} minHeight={loaderMinHeight}>
                <CarouselContent>
                  {cards.map((child, index) => (
                    <CarouselItem
                      key={`${child.id || child.type}-${index}`}
                      data-testid={TEST_LOCATORS.SECTION_PRODUCT(sectionTestId, index + 1)}
                    >
                      {/* Reco carousel: hide swatches + ratings (non-PL parity). */}
                      <CardDisplayOptionsProvider
                        hideOptions
                        hideReviews={isShopTheLookBand}
                        useRecommendationImageAspect={false}
                      >
                        {child.node}
                      </CardDisplayOptionsProvider>
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </LoaderWrapper>
            </div>
          </Carousel>
        ) : (
          // Mobile reuses the shared `MobileScrollCarousel` (the non-PL `product-list-section-two`
          // "More to explore" block) rather than the Embla carousel: it is a NATIVE scroll-snap track
          // (smooth on touch, no per-frame JS on these heavy cards) and it owns its own dot row. The
          // web-ui `CarouselIndicator` this replaces paints WHITE dots (`selected: white`,
          // `unselected: white-trans-50` — built to overlay imagery), which were invisible on this
          // white band; the shared component's dots are black/lightgray.
          <div className={carouselWrapperClass}>
            <div className={headerRowClass}>{heading}</div>
            <LoaderWrapper showLoader={isRefreshing} minHeight={loaderMinHeight}>
              <MobileScrollCarousel
                dotsRowCss={mobileDotsRowCssRaw}
                // Band gutter is the `space-16` this expects; the track runs edge to edge and each page
                // carries the gutter instead — STRFRNT-16548.
                fullBleed
                pages={mobilePages.map((page, pageIndex) => (
                  // Keyed by chunk position: these reco cards carry inline data and arrive with an
                  // empty `id`, so an id/type-derived key is identical across same-sized pages.
                  <div key={pageIndex} className={mobileGridClass}>
                    {page.map((child, index) => (
                      <div
                        key={`${child.id || child.type}-${index}`}
                        data-testid={TEST_LOCATORS.SECTION_PRODUCT(
                          sectionTestId,
                          pageIndex * MOBILE_CAROUSEL_PAGE_SIZE + index + 1,
                        )}
                      >
                        <CardDisplayOptionsProvider
                          hideOptions
                          hideReviews={isShopTheLookBand}
                          useRecommendationImageAspect={false}
                        >
                          {child.node}
                        </CardDisplayOptionsProvider>
                      </div>
                    ))}
                  </div>
                ))}
              />
            </LoaderWrapper>
          </div>
        )}
      </section>
    </CardTrackingProvider>
  )
}

const carouselPlaceholder = <div style={{ height: '58rem', width: '100%' }} />

export const ProductGridCarouselPdpWidget: PLTypedWidgetInterface<
  ProductGridCarouselWidgetData,
  WidgetType.PRODUCT_GRID_CAROUSEL
> = {
  id: WidgetType.PRODUCT_GRID_CAROUSEL,
  errorBoundary: PLBaseErrorBoundary,
  render: ProductGridCarouselPdp,
  suspenseFallback: carouselPlaceholder,
  // No cards → render() returns null; skip the shell so no empty `<div data-widget-type>` is emitted.
  isEmpty: props => (props.renderedChildren?.length ?? 0) === 0,
}
